import { TableBody, TableCell, TableHead, Table as TableMui, TableRow as TableRowMui, styled } from "@mui/material";
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { HiddenDiv } from "./common";

const StatusTh = styled(TableCell)({
  width: "200px",
  textAlign: "right",
  maxWidth: "200px",
});

const StatusTd = styled(TableCell)({
  textAlign: "right",
  padding: "0px",
});

const TableRow = styled(TableRowMui)({
  minHeight: "40px",
  maxHeight: "40px",
  height: "40px",

  [`&:hover ${HiddenDiv}`]: {
    visibility: "visible",
  },
});

const TableRowHead = styled(TableRowMui, {
  shouldForwardProp: (prop) => prop !== "sticky",
})<{
  sticky?: boolean;
}>(({ sticky = false, theme }) => {
  return {
    fontWeight: "bold",
    paddingBottom: "20px",
    paddingTop: "5px",
    fontSize: "11px",
    verticalAlign: "top",
    ...(sticky && {
      position: "sticky",
      "&.isSticky": {
        top: "20px",
        backgroundColor: theme.palette.primary.main,
        // if not it overlaps with status col for example
        zIndex: 1,
        height: "40px",
        borderRadius: 0,
        " > th": {
          color: theme.palette.surfaceLight10.main,
        },
      },
    }),
  };
});

const TableRowHover = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "disableHover",
})<{
  disableHover?: boolean;
}>(({ disableHover = "horizontal", theme }) => {
  return {
    cursor: disableHover ? "initial" : "pointer",
    "&:hover": {
      backgroundColor: theme.palette.surfaceLight20.main,
    },
    "&.selected": {
      backgroundColor: theme.palette.surfaceLight20.main,
    },
  };
});

const FixedWithCol = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "width" && prop !== "left",
})<{
  width?: string;
  left?: boolean;
}>(({ width, left }) => {
  return {
    width: width || "150px",
    textAlign: left ? "left" : "center",
    maxWidth: width || "150px",
  };
});

const DateCol = styled(TableCell)({
  width: "135px",
  fontWeight: "bold",
});

const Table = styled(TableMui)({
  borderCollapse: "collapse",
  padding: "0px 20px",
  width: "100%",
  marginBottom: "20px",
  maxWidth: "100%",
  backgroundColor: "transparent",
});

interface MuiTableProps {
  id?: string;
  testLabel?: string;
  hover?: boolean;
  striped?: boolean;
  className?: string;
  header?: ReactNode;
  body: ReactNode;
  withStickyHeader?: boolean;
}

const DefaultTableMui: FC<MuiTableProps> = ({ id, testLabel = "default-table", header, body, withStickyHeader }) => {
  const [isSticky, setIsSticky] = useState(false);
  const ref = useRef<HTMLTableSectionElement | null>(null);

  const checkStickyHeader = useCallback(() => {
    const cachedRef = ref.current;
    if (withStickyHeader && cachedRef) {
      const observer = new IntersectionObserver(([e]) => setIsSticky(e.intersectionRatio < 1), {
        threshold: [1],
        rootMargin: "-1px 0px 0px 0px",
      });

      observer.observe(cachedRef);
      return function () {
        observer.unobserve(cachedRef);
      };
    }
  }, [withStickyHeader]);
  useEffect(() => {
    return checkStickyHeader();
  }, [checkStickyHeader]);
  return (
    <Table
      sx={{ minWidth: 650, position: "relative" }}
      aria-label="simple table"
      id={id}
      data-cy={testLabel}
      data-testid={testLabel}
    >
      <TableHead ref={ref}>
        <TableRowHead className={isSticky ? "isSticky" : ""} sticky={withStickyHeader}>
          {header}
        </TableRowHead>
      </TableHead>
      <TableBody>{body}</TableBody>
    </Table>
  );
};

export { DateCol, DefaultTableMui, FixedWithCol, StatusTd, StatusTh, TableCell, TableRow, TableRowHover };
